import { FileDownloadOutlined } from '@mui/icons-material';
import { Permissions, useScopeChecker } from 'src/rbac';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  CardHeader,
  Checkbox,
  Chip,
  Grid,
  Alert,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Skeleton,
  Snackbar,
  TableContainer,
  TableSortLabel,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { format } from 'date-fns';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { SignatureAction } from 'src/types/signatureContext';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import MySignatureModal from 'src/components/document-management/my-bulk-signature-modal';
import SigningLockedDialog from 'src/components/signature/signing-locked-dialog';
import { useSignatureValidation } from 'src/hooks/use-signature-validation';
import useCurrentUser from 'src/hooks/use-current-user';
import useClockInOutButtonVisibility from 'src/hooks/use-clock-in-out-button-visibility';
import RightIcon from 'src/icons/untitled-ui/duocolor/rightIcon';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import useEffectAfterMount from 'src/layouts/components/useEffectAfterMount';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { paths } from 'src/paths';
import dashboardServices from 'src/services/apiServices/dashboard-services/dashboard-services';
import {
  getTherapistDocumentCreateEditDataAction,
  resetreAssignTherapistAction,
  resetTherapistDocumentCreateEditDataAction,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import {
  getAllPatientsByAction,
  getPatientIdAction,
  resetPatientRecordAction,
  switchTherapistDisciplineAction,
} from 'src/redux/actions/patientAction';
import {
  applyBulkSignatureReset,
  getMyOtherDashboardDocuments,
  previewDashboardDoc,
  resetAllMyDashboardDocuments,
} from 'src/redux/actions/therapist-dashboard-action';
import useDialogVisibility from 'src/utils/dialog-hide';
import { DocumentIcons } from '../../dashboard-icon';
import ClockInOutModal from '../../time-in-out/clock-in-out-modal';
import { documentTypes } from '../document-manegment-const';
import { MenuDateFilter } from '../menu-date-filter';
import MultiSelectWithSearch from '../table-header-multiselect-search';
import { TableHeaderMultiselect } from '../table-hearder-multiselect';
import SelectSupervisior from './SelectSupervisor';
import { modalStyle } from 'src/utils/common-properties';
import SelectSuperVisor from 'src/components/document-management/select-supervisor';
import emptyImg from 'src/pages/therapist-dashboard/therapist-document/image-removebg-preview.png';

const SignIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M12.9264 3.05139L14.3326 1.64515C14.9428 1.03495 15.9322 1.03495 16.5424 1.64515C17.1526 2.25534 17.1526 3.24466 16.5424 3.85485L7.69349 12.7037C7.25292 13.1443 6.70953 13.4681 6.11241 13.646L3.875 14.3125L4.54148 12.0751C4.71935 11.478 5.04321 10.9346 5.48377 10.494L12.9264 3.05139ZM12.9264 3.05139L15.125 5.24999M13.875 10.9792V14.9375C13.875 15.973 13.0355 16.8125 12 16.8125H3.25C2.21447 16.8125 1.375 15.973 1.375 14.9375V6.18749C1.375 5.15196 2.21447 4.31249 3.25 4.31249H7.20833"
        stroke="white"
        stroke-width="1.3125"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

const MySignatureManagement: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isRunning, startStopwatch } = useStopwatch();

  const { canShowDialog: canShowClockInDialog } = useDialogVisibility('clock_in_do_not_show');

  const getDocumentRes = useSelector((state: any) => state.myDashboardDocumentListReducer);
  const getAllMyDocuments = useSelector(
    (state: any) => state.myDashboardDocumentListReducer?.response?.data || []
  );
  const reAssigneResponse = useSelector((state: any) => state.reAssigntherapistReducer);
  const callSubmitEventReducer = useSelector((state: any) => state.callSubmitEventReducer);
  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);
  const [providerId, firstName, lastName] = useCurrentUser(['providerId', 'firstName', 'lastName']);
  const { patients, patientDetails, episodeOfCare, details } = paths;

  const has = useScopeChecker();
  const totalPatientRes = useSelector((state: any) => state.getAllPatientsReducer);

  // APIT-587: clock in/out warning pop-up (document editing) gated by the
  // per-facility flag (single source of truth) instead of the enterprise flags.
  const { refetch: refetchClockInOutVisibility } = useClockInOutButtonVisibility();

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'dueDate',
    direction: 'desc',
  });
  const [signDocument, setSignDocument] = useState<any[]>([]);
  const [coSignDocument, setCosignDocument] = useState<any[]>([]);
  const selectAllDocuments = (e: any) => {
    // Select-all picks docs the current user can sign right now. The BE's
    // allowedTherapistSignatureActions has CAN_SIGN iff the user is the assigned therapist
    // AND the doc is unsigned AND any preconditions are met. Replaces the previous
    // signatureStatus + supervisorId + supervisorSignStatus combination predicate.
    const filterMyDoc = getAllMyDocuments?.authoredDocuments?.filter(
      (doc: any) =>
        doc?.signatureContext?.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_SIGN)
    );
    // THIN-7261: superviseStatus is being phased out for FE use; read the canonical
    // approvedForSupervisorSignature boolean instead. The BE dual-writes both so the
    // values agree, but the boolean is the contract going forward.
    const filterCosine = getAllMyDocuments?.coSignatureDocuments?.filter(
      (doc: any) => doc.approvedForSupervisorSignature === true
    );
    const isChecked = e.target.checked;

    if (isChecked) {
      setSignDocument(filterMyDoc);
      setCosignDocument(filterCosine);
      return;
    } else {
      setSignDocument([]);
      setCosignDocument([]);
    }
  };

  const handleAddDocument = (e: React.ChangeEvent<HTMLInputElement>, document: any) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setSignDocument((prev) => {
        const isDocumentExists = prev.some((doc) => doc.uuid === document.uuid);
        if (!isDocumentExists) {
          return [...prev, document];
        }
        return prev;
      });
    } else {
      setSignDocument((prev) => prev.filter((doc) => doc.uuid !== document.uuid));
    }
  };

  const handleAddCoSignDocument = (e: React.ChangeEvent<HTMLInputElement>, document: any) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      setCosignDocument((prev) => {
        const isDocumentExists = prev.some((doc) => doc.uuid === document.uuid);
        if (!isDocumentExists) {
          return [...prev, document];
        }
        return prev;
      });
    } else {
      setCosignDocument((prev) => prev.filter((doc) => doc.uuid !== document.uuid));
    }
  };

  const isBulkSignatureSigned = useSelector((state: any) => state.therapistBulkSignatureReducer);
  const [popUpMessage, setPopUpMessage] = useState({ type: '', message: '' });

  //clock in modal

  const [isClockIn, setIsClockIn] = useState(false);
  const [notToShowAgain, setNotToShowAgain] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>('');
  const [openNewSignModel, setOpenNewSignModel] = useState(false);
  const { enabled: signatureValidationEnabled } = useSignatureValidation();
  const [bulkPinError, setBulkPinError] = useState<string | null>(null);
  const [bulkLocked, setBulkLocked] = useState<{ open: boolean; until: string | null }>({
    open: false,
    until: null,
  });

  const previewDocument = (formId: any) => {
    dispatch(previewDashboardDoc(formId));
    navigate('/dashboard/doc');
  };

  const [actionToBeContinue, setActionToBeContinue] = useState('');

  const handleDocumentEdit = useCallback(
    async (document: any) => {
      setSelectedDocument(document);
      setActionToBeContinue('EDIT_MY_DOCUMENT');

      // Re-fetch the per-facility flag NOW so this edit uses the latest value
      // (no page/tab refresh needed). Any error/404 -> false (hide).
      const canShowClockInOut = await refetchClockInOutVisibility();
      if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
        setIsClockIn(true);
        return;
      }
      dispatch(resetTherapistDocumentCreateEditDataAction());
      dispatch(getTherapistDocumentCreateEditDataAction(document));
      navigate('/document/document-edit', {
        state: {
          document,
        },
      });
    },
    [isRunning, notToShowAgain, refetchClockInOutVisibility]
  );

  const checkEditPermission = (document: any) => {
    // Prefer signatureContext (THIN-7160) — CAN_EDIT on the therapist axis for the form
    // author or on the supervisor axis for the assigned cosigner doing a cosigner-edit
    // (THIN-6967). Falls back to legacy form-type-permission gates when ctx is missing.
    const ctx = document?.signatureContext;
    if (ctx) {
      return (
        ctx.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_EDIT) ||
        ctx.allowedSupervisorSignatureActions?.includes(SignatureAction.CAN_EDIT)
      );
    }
    if (
      document.formType?.includes('RECERTIFICATION') &&
      !has(Permissions.UPDATE_RECERTIFICATION)
    ) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !has(Permissions.UPDATE_EVAL)) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (checkoutResponse.isGetCheckIn && checkoutResponse?.response?.content !== null) {
      if (checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS') {
        if (actionToBeContinue === 'EDIT_MY_DOCUMENT') {
          dispatch(resetTherapistDocumentCreateEditDataAction());
          dispatch(getTherapistDocumentCreateEditDataAction(selectedDocument));
          navigate('/document/document-edit', {
            state: {
              document: selectedDocument,
            },
          });
        }
        if (actionToBeContinue === 'BULK_SIGN') {
          setOpenNewSignModel(true);
        }
      }
    }
  }, [checkoutResponse]);

  // Bulk "Sign Selected Docs" — re-fetch the per-facility flag NOW so this
  // operation uses the latest value. Any error/404 -> false (hide). If the
  // clock-in popup should show, defer opening the sign modal until the user
  // resolves the popup (handleIsClock / post-clock-in effect resume BULK_SIGN).
  const handleSignSelectedDocs = async () => {
    setActionToBeContinue('BULK_SIGN');
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }
    setOpenNewSignModel(true);
  };

  const handleIsClock = () => {
    if (actionToBeContinue === 'EDIT_MY_DOCUMENT') {
      dispatch(resetTherapistDocumentCreateEditDataAction());
      dispatch(getTherapistDocumentCreateEditDataAction(selectedDocument));
      navigate('/document/document-edit', {
        state: {
          document: selectedDocument,
        },
      });
    }
    if (actionToBeContinue === 'BULK_SIGN') {
      setOpenNewSignModel(true);
    }
    setNotToShowAgain(true);
    setIsClockIn(false);
  };

  useEffect(() => {
    if (providerId) {
      dispatch(resetAllMyDashboardDocuments());
      setSelectedTherapist([
        {
          label: `${firstName} ${lastName}`,
          value: providerId,
        },
      ]);
    }
  }, [providerId]);

  const goToEoc = (document: any) => {
    dispatch(getPatientIdAction(document?.patientId));
    dispatch(switchTherapistDisciplineAction(''));
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: document?.episodeOfCareId,
        getPatientId: document?.patientId,
      },
    });
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [getCurrentItem, setGetCurrentItem] = useState<any>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    setGetCurrentItem(null);
  };

  const HandleStartClock = () => {
    const millageInfo = {
      locationType: 'BUILDING',
      payByMilage: null,
    };
    startStopwatch('MILLAGE', millageInfo);
  };
  const listOfTherapistAvailability = useSelector(
    (state: any) => state.getListOfTherapistAvaiReducer
  );
  const [selectedTherapist, setSelectedTherapist] = useState<any>([]);

  const [selectedDocumentType, setSelectedDocumentType] = useState<any>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>([]);
  const [selectedDueDate, setSelectedDueDate] = useState<any>({
    startDate: '',
    endDate: '',
  });

  const getListOfValues = (items: any) => {
    return items?.map((therapist: any) => therapist?.value);
  };

  const buildDocumentFilters = (page: number) => ({
    page: page,
    therapistIds: selectedTherapist?.length
      ? getListOfValues(selectedTherapist)
      : getListOfValues(getTherapistList),
    pageSize: 20,
    documentTypes: selectedDocumentType?.length ? getListOfValues(selectedDocumentType) : null,
    types: null,
    dueStatus: null,
    startDate: selectedDueDate?.startDate
      ? moment(selectedDueDate?.startDate).format('YYYY-MM-DD')
      : null,
    endDate: selectedDueDate?.endDate
      ? moment(selectedDueDate?.endDate).format('YYYY-MM-DD')
      : null,
    patientIds: selectedPatient?.length ? getListOfValues(selectedPatient) : null,
    sortBy: sortConfig?.key,
    sortDirection: sortConfig?.direction,
  });

  const getAllDocumentApiCall = (page: number) => {
    dispatch(getMyOtherDashboardDocuments({ param: buildDocumentFilters(page) }));
  };

  useEffectAfterMount(() => {
    // if (selectedTherapist?.length) {
    dispatch(resetAllMyDashboardDocuments());
    getAllDocumentApiCall(0);
    // }
  }, [selectedDocumentType, selectedDueDate, selectedPatient, selectedTherapist, sortConfig]);

  const getTherapistList = useMemo(() => {
    const mappedTherapists = listOfTherapistAvailability?.response?.data?.map((res: any) => {
      return {
        label:
          res?.uuid === providerId
            ? `${res?.lastName}, ${res?.firstName} (Me)`
            : `${res?.lastName}, ${res?.firstName}`,
        value: res?.uuid,
      };
    });

    return mappedTherapists?.sort((a: any, b: any) => {
      return a?.label.localeCompare(b?.label);
      // if (a?.value === providerId) return -1;
      // if (b?.value === providerId) return 1;
      // return 0;
    });
  }, [listOfTherapistAvailability, providerId]);

  const [pageNo, setPageNo] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'csv' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(resetPatientRecordAction());
    dispatch(
      getAllPatientsByAction({
        data: {
          searchString: '',
        },
        params: {
          page: 0,
          pageSize: 20,
          active: true,
        },
      })
    );
    dispatch(resetUuidAction());
  }, []);
  const getAllPatientList = useMemo(() => {
    return totalPatientRes?.totalPatients.map((res: any) => {
      return {
        label: `${res?.lastName}, ${res?.firstName}`,
        value: res?.uuid,
      };
    });
  }, [totalPatientRes]);

  const handleSort = (key: string) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === 'asc';
    const direction = isAsc ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const fileNameFromDisposition = (response: any): string | null => {
    const disposition = response?.headers?.['content-disposition'];
    return disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? null;
  };

  // A CSV error arrives as a Blob because the request asked for one; read the message back out.
  const readErrorMessage = async (error: any): Promise<string | null> => {
    const body = error?.response?.data;
    if (!body) return null;
    if (body instanceof Blob) {
      try {
        return JSON.parse(await body.text())?.message ?? null;
      } catch {
        return null;
      }
    }
    return body?.message ?? null;
  };

  const handleExport = async (format: 'pdf' | 'csv') => {
    setExportMenuAnchor(null);
    setExportingFormat(format);
    try {
      const response = await dashboardServices.exportMyDashboardDocuments(
        { param: buildDocumentFilters(0) },
        format
      );

      // The axios interceptor resolves with `error.response` instead of rejecting, so a
      // failed export (e.g. 400 "narrow the filters") lands here; route it to the catch.
      if (!response || response.status >= 400) throw { response };

      if (format === 'csv') {
        const url = URL.createObjectURL(new Blob([response?.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNameFromDisposition(response) ?? 'My_Documents.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        const saveBlob = (blob: Blob, name: string) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        };
        const fileName = fileNameFromDisposition(response) ?? 'My_Documents.pdf';
        const body: Blob = response?.data;
        if (body?.type?.includes('pdf')) {
          saveBlob(body, fileName);
        } else {
          // JSON envelope carrying a link to the PDF.
          const pdfUrl = JSON.parse(await body.text())?.data;
          if (pdfUrl) {
            try {
              saveBlob(await (await fetch(pdfUrl)).blob(), fileName);
            } catch {
              // Fetch blocked (e.g. CORS) — fall back to opening the file.
              window.open(pdfUrl, '_blank', 'noopener');
            }
          }
        }
      }
    } catch (error: any) {
      setExportError(
        (await readErrorMessage(error)) ??
          `Could not generate the ${format.toUpperCase()}. Please try again.`
      );
    } finally {
      setExportingFormat(null);
    }
  };
  const handleSortMultiselect = useCallback((label: any, direction: any) => {
    if (label === 'Therapist') return setSortConfig({ key: 'therapistName', direction });
    if (label === 'Document Type') return setSortConfig({ key: 'documentType', direction });
    if (label === 'Discipline') return setSortConfig({ key: 'discipline', direction });
    if (label === 'Status') return setSortConfig({ key: 'dueStatus', direction });
    if (label === 'PatientName') return setSortConfig({ key: 'patientName', direction });
  }, []);

  const handleCloseNewSignModel = () => {
    setOpenNewSignModel(false);
    setBulkPinError(null);
    // dispatch(resetNewDocumentSignAction());
  };

  useEffect(() => {
    if (isBulkSignatureSigned?.response === null && !isBulkSignatureSigned?.isBulkSignature) return;
    if (isBulkSignatureSigned?.response !== null && !isBulkSignatureSigned?.isBulkSignature) {
      //  THIN-7681: a signing-PIN challenge failure carries a structured outcome (THIN-7706) —
      //  keep the modal open for a retry (WRONG_PIN / NO_PIN_SET) or show the lockout (LOCKED),
      //  instead of the generic close-and-toast path below.
      const challenge = signatureValidationEnabled ? isBulkSignatureSigned?.response?.data : null;
      const outcome = challenge?.outcome;
      if (outcome === 'LOCKED') {
        setBulkLocked({ open: true, until: challenge?.lockedUntil ?? null });
        setOpenNewSignModel(false);
        dispatch(applyBulkSignatureReset());
        return;
      }
      if (outcome === 'WRONG_PIN') {
        const left = challenge?.attemptsRemaining;
        setBulkPinError(
          `Incorrect PIN.${
            left != null ? ` ${left} attempt${left === 1 ? '' : 's'} remaining.` : ''
          }`
        );
        dispatch(applyBulkSignatureReset());
        return;
      }
      if (outcome === 'NO_PIN_SET') {
        setBulkPinError('No signing PIN set yet. Set one up in Profile → Signature & PIN.');
        dispatch(applyBulkSignatureReset());
        return;
      }

      //  ERROR OCCURRED
      setOpenNewSignModel(false);
      setPopUpMessage({ message: isBulkSignatureSigned?.response?.message, type: 'ERROR' });
      setSignDocument([]);
      setCosignDocument([]);
      setTimeout(() => {
        dispatch(applyBulkSignatureReset());
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);

      return;
    }

    //SUCCESS signature signed successfully
    dispatch(resetAllMyDashboardDocuments());
    getAllDocumentApiCall(0);
    setOpenNewSignModel(false);
    setPopUpMessage({ message: 'Document Signed Successfully', type: '' });
    setSignDocument([]);
    setCosignDocument([]);
    setTimeout(() => {
      dispatch(applyBulkSignatureReset());
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 3000);
  }, [isBulkSignatureSigned]);

  useEffect(() => {
    if (!reAssigneResponse.isReAssign && reAssigneResponse.response === null) return;
    dispatch(resetAllMyDashboardDocuments());
    getAllDocumentApiCall(0);
    dispatch(resetreAssignTherapistAction());
  }, [reAssigneResponse]);

  useEffect(() => {
    if (!callSubmitEventReducer?.isEventCalled) return;
    dispatch(resetAllMyDashboardDocuments());
    getAllDocumentApiCall(0);
  }, [callSubmitEventReducer]);
  const checkWhosSignNeed = (document: any) => {
    if (
      document?.documentType.toLowerCase().includes('evaluation') ||
      document?.documentType.toLowerCase().includes('recertification')
    ) {
      return 'Physician';
    } else {
      return 'Supervisor';
    }
  };
  const [role, selectRole] = useState('');
  const [triggerSelectSupervisor, setTriggerSupervisor] = useState(false);

  const openSelectSupervisorModal = (document: any, role: string) => {
    setSelectedDocument(document);
    selectRole(role);
    setTriggerSupervisor(true);
  };

  const closeSupervisorModal = () => {
    setTriggerSupervisor(false);
  };
  const providerInfo = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );

  return (
    <>
      <Card>
        <CardHeader
          title={
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6">My Signature Pending</Typography>
                  {getDocumentRes?.loading ? (
                    <Box sx={{ width: 100, ml: 3 }}>
                      <Typography
                        width={'100%'}
                        variant="h4"
                      >
                        <Skeleton animation="pulse" />
                      </Typography>
                    </Box>
                  ) : (
                    <Chip
                      label={`${
                        getAllMyDocuments?.authoredDocuments?.length +
                        getAllMyDocuments?.coSignatureDocuments?.length
                          ? getAllMyDocuments?.authoredDocuments?.length +
                            getAllMyDocuments?.coSignatureDocuments?.length
                          : '0'
                      } Document`}
                      variant="outlined"
                      sx={{ marginLeft: '10px' }}
                    />
                  )}
                </Box>
                <Stack
                  gap={2}
                  direction="row"
                >
                  <Box>
                    <LoadingButton
                      startIcon={<FileDownloadOutlined />}
                      size="large"
                      sx={{
                        whiteSpace: 'nowrap',
                        height: 35,
                        width: 120,
                        '.MuiLoadingButton-loadingIndicator': {
                          left: 15,
                        },
                      }}
                      loadingPosition="start"
                      loading={!!exportingFormat}
                      disabled={!!exportingFormat || getDocumentRes?.loading}
                      onClick={(event) => setExportMenuAnchor(event.currentTarget)}
                    >
                      Download
                    </LoadingButton>
                    <Menu
                      anchorEl={exportMenuAnchor}
                      open={Boolean(exportMenuAnchor)}
                      onClose={() => setExportMenuAnchor(null)}
                      PaperProps={{ sx: { width: 120 } }}
                    >
                      <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
                      <MenuItem onClick={() => handleExport('csv')}>CSV</MenuItem>
                    </Menu>
                  </Box>
                  <Box>
                    <LoadingButton
                      disabled={!(coSignDocument?.length || signDocument?.length)}
                      startIcon={<SignIcon />}
                      variant="contained"
                      size="large"
                      sx={{
                        whiteSpace: 'nowrap',
                        height: 35,
                        ':hover': {
                          background: '#6366F1',
                        },
                        '.MuiLoadingButton-loadingIndicator': {
                          left: 15,
                        },
                      }}
                      loadingPosition="start"
                      loading={getDocumentRes?.loading}
                      onClick={handleSignSelectedDocs}
                    >
                      Sign Selected Docs
                    </LoadingButton>
                  </Box>
                </Stack>
              </Box>
            </>
          }
        />

        {/* <Scrollbar> */}
        <>
          <Stack gap="1rem">
            <TableContainer component={Paper}>
              <Table
                stickyHeader
                sx={{ minWidth: 700, tableLayout: 'fixed' }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Checkbox onClick={(e: any) => selectAllDocuments(e)} />
                      <TableSortLabel
                        active={sortConfig.key === 'patientName'}
                        direction={sortConfig.key === 'patientName' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('patientName')}
                      >
                        Patient
                      </TableSortLabel>
                      <MultiSelectWithSearch
                        options={getAllPatientList}
                        pageNo={pageNo}
                        setPageNo={setPageNo}
                        handleSortMultiselect={handleSortMultiselect}
                        setAppliedValues={setSelectedPatient}
                        appliedValues={selectedPatient}
                        sortConfig={sortConfig}
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'documentType'}
                        direction={sortConfig.key === 'documentType' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('documentType')}
                      >
                        Document Type
                      </TableSortLabel>
                      <TableHeaderMultiselect
                        label="Document Type"
                        sortConfig={sortConfig}
                        appliedValues={selectedDocumentType}
                        setAppliedValues={setSelectedDocumentType}
                        options={documentTypes}
                        handleSortMultiselect={handleSortMultiselect}
                      />
                    </TableCell>

                    <TableCell>Author</TableCell>

                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'dueStatus'}
                        direction={sortConfig.key === 'dueStatus' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('dueStatus')}
                      >
                        Submitted Date
                      </TableSortLabel>
                      <MenuDateFilter
                        label="Submitted Date"
                        setSelectedDueDate={setSelectedDueDate}
                        selectedDueDate={selectedDueDate}
                      />
                    </TableCell>

                    <TableCell>Signed Date</TableCell>

                    <TableCell>Status</TableCell>

                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody sx={{ maxHeight: 500, overflowY: 'auto' }}>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Authored documents({getAllMyDocuments?.authoredDocuments?.length || 0}){' '}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ padding: 0 }}
                    >
                      <Box sx={{ maxHeight: 550, overflowY: 'auto' }}>
                        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableBody>
                            {getDocumentRes?.loading ? (
                              <TableCell colSpan={6}>
                                {Array.from({ length: 7 }).map((_, index) => (
                                  <Box
                                    textAlign="center"
                                    key={index}
                                  >
                                    <Typography
                                      variant="h4"
                                      color="initial"
                                    >
                                      <Skeleton animation="wave" />
                                    </Typography>
                                  </Box>
                                ))}
                              </TableCell>
                            ) : !getDocumentRes?.response?.data?.authoredDocuments?.length ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Box
                                    textAlign="center"
                                    p={3}
                                  >
                                    <img
                                      src={emptyImg}
                                      alt="loader"
                                      style={{
                                        width: 620,
                                        height: 240,
                                        margin: 'auto',
                                        alignItems: 'center',
                                        alignContent: 'center',
                                        marginTop: 50,
                                        display: 'block',
                                      }}
                                    />
                                    <Typography
                                      color={'text.secondary'}
                                      variant="h6"
                                      fontWeight={'200'}
                                    >
                                      No results found
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              getAllMyDocuments?.authoredDocuments?.map(
                                (document: any, index: any) => {
                                  return (
                                    <TableRow
                                      hover
                                      key={index}
                                    >
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Checkbox
                                            disabled={
                                              !document?.signatureContext?.allowedTherapistSignatureActions?.includes(
                                                SignatureAction.CAN_SIGN
                                              )
                                            }
                                            checked={signDocument?.some(
                                              (item: any) => item?.uuid === document?.uuid
                                            )}
                                            onClick={(e: any) => handleAddDocument(e, document)}
                                          />
                                          <Typography variant="subtitle2">
                                            {document?.patientName}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Stack
                                          alignItems="center"
                                          direction="row"
                                          spacing={1}
                                        >
                                          <Grid
                                            container
                                            sx={{ alignItems: 'center' }}
                                          >
                                            <Box sx={{ marginRight: '4px', marginTop: '7px' }}>
                                              <DocumentIcons
                                                iconType={document?.formType
                                                  ?.split('_')?.[0]
                                                  ?.toLowerCase()}
                                              />
                                            </Box>

                                            <Box sx={{ display: 'flex' }}>
                                              <Typography variant="subtitle2">
                                                {document?.documentType}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        </Stack>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="subtitle2">
                                          {document?.providerName}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="subtitle2">
                                          {document?.dateOfService
                                            ? format(
                                                new Date(
                                                  new Date(document?.dateOfService)?.valueOf() +
                                                    new Date(
                                                      document?.dateOfService
                                                    )?.getTimezoneOffset() *
                                                      60 *
                                                      1000
                                                ),
                                                'dd MMM, yyyy'
                                              )
                                            : '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="subtitle2">
                                          {document?.signedDate || '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', ml: '-20px' }}>
                                          {(() => {
                                            // THIN-7234: chip describes the missing axis
                                            // explicitly. checkWhosSignNeed returned one role per
                                            // document type and missed the "EVAL needs both
                                            // supervisor AND physician" case.
                                            // THIN-7257: "missing" means no assignee yet —
                                            // CAN_SELECT_* in allowed actions is also emitted
                                            // during DRAFT for the swap affordance (THIN-7214)
                                            // and stayed true after a successful assign.
                                            // THIN-7252: show one missing label at a time,
                                            // supervisor first (matches the FSM's
                                            // supervisor-before-physician ordering rule).
                                            const supRequired =
                                              !!document?.signatureContext
                                                ?.supervisorSignatureRequired;
                                            const phyRequired =
                                              !!document?.signatureContext
                                                ?.physicianSignatureRequired;
                                            const missingSup =
                                              supRequired &&
                                              !document?.signatureContext?.supervisor?.uuid;
                                            const missingPhys =
                                              phyRequired &&
                                              !document?.signatureContext?.physician?.uuid;
                                            const missingLabel = missingSup
                                              ? 'Missing Supervisor'
                                              : missingPhys
                                              ? 'Missing Physician'
                                              : null;
                                            if (missingLabel) {
                                              return (
                                                <Chip
                                                  label={
                                                    <Typography variant="overline">
                                                      {missingLabel}
                                                    </Typography>
                                                  }
                                                  variant="outlined"
                                                  sx={{
                                                    marginLeft: '10px',
                                                    color: '#B54708',
                                                    backgroundColor: '#F790091F',
                                                  }}
                                                />
                                              );
                                            }
                                            return (
                                              <Chip
                                                label={
                                                  <Typography variant="overline">
                                                    Pending Signature
                                                  </Typography>
                                                }
                                                variant="outlined"
                                                sx={{
                                                  marginLeft: '10px',
                                                  color: '#6C737F',
                                                  backgroundColor: 'rgba(17, 25, 39, 0.08)',
                                                }}
                                              />
                                            );
                                          })()}
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        sx={{ maxWidth: 200 }}
                                        align="right"
                                      >
                                        <Stack
                                          flexDirection={'row'}
                                          justifyContent={'flex-end'}
                                          sx={{ maxWidth: 200 }}
                                        >
                                          {/* THIN-7234: emit per-axis Assign buttons so EVAL/RECERT
                                                forms that need both a supervisor AND a physician
                                                surface both affordances (the previous single-button
                                                derived role from checkWhosSignNeed could only offer
                                                one axis per document type).
                                                THIN-7252: when supervisor is required and not yet
                                                assigned, suppress the Physician cell — the user
                                                picks supervisor first, then physician. Once a
                                                supervisor is assigned the physician cell appears
                                                (Assign button if physician missing, name + Link if
                                                already assigned). Matches the FSM's
                                                supervisor-before-physician ordering rule. */}
                                          {(() => {
                                            const ctx = document?.signatureContext;
                                            const supRequired = !!ctx?.supervisorSignatureRequired;
                                            // "Assigned" means uuid AND a renderable name — the THIN-7246
                                            // archived-physician path keeps uuid but null name, which
                                            // SelectSupervisor falls back to as an Assign affordance.
                                            // Don't treat that as "assigned" or we'd hide the affordance
                                            // the user still needs.
                                            const supAssigned =
                                              !!ctx?.supervisor?.uuid && !!ctx?.supervisor?.name;
                                            const phyAssigned =
                                              !!ctx?.physician?.uuid && !!ctx?.physician?.name;
                                            const canSelectSup =
                                              ctx?.allowedSupervisorSignatureActions?.includes(
                                                SignatureAction.CAN_SELECT_SUPERVISOR
                                              );
                                            const canSelectPhy =
                                              ctx?.allowedPhysicianSignatureActions?.includes(
                                                SignatureAction.CAN_SELECT_PHYSICIAN
                                              );
                                            // THIN-7252: physician affordance is hidden until supervisor is
                                            // picked when both are required (supervisor-first ordering).
                                            // THIN-7262: hide each axis's cell as soon as that axis is
                                            // assigned — even a single name crowds the row, and the row's
                                            // signed-state and Missing chip already communicate which
                                            // cosigners remain outstanding. Original THIN-7262 scope only
                                            // hid the physician once both were assigned; broadened here
                                            // per product to hide both axes individually on assignment.
                                            // Reassignment from this view goes away when assigned — same
                                            // trade-off the original "both assigned" rule already made.
                                            const showSupervisorCell = canSelectSup && !supAssigned;
                                            const showPhysicianCell =
                                              canSelectPhy &&
                                              (!supRequired || supAssigned) &&
                                              !phyAssigned;
                                            return (
                                              <>
                                                {showSupervisorCell && (
                                                  <SelectSupervisior
                                                    role={'Supervisor'}
                                                    document={document}
                                                    openSelectSupervisorModal={
                                                      openSelectSupervisorModal
                                                    }
                                                    who={'supervisor'}
                                                    from="MY_SIGN"
                                                  />
                                                )}
                                                {showPhysicianCell && (
                                                  <SelectSupervisior
                                                    role={'Physician'}
                                                    document={document}
                                                    openSelectSupervisorModal={
                                                      openSelectSupervisorModal
                                                    }
                                                    who={'physician'}
                                                    from="MY_SIGN"
                                                  />
                                                )}
                                              </>
                                            );
                                          })()}

                                          <Button
                                            title="Complete Document"
                                            onClick={() => previewDocument(document)}
                                            size="small"
                                            sx={{
                                              color: ' #6366F1',
                                              fontWeight: 600,
                                              fontSize: '13px',
                                            }}
                                          >
                                            View
                                          </Button>
                                          {checkEditPermission(document) && (
                                            <Button
                                              title="Complete Document"
                                              onClick={() => handleDocumentEdit(document)}
                                              size="small"
                                              sx={{
                                                color: ' #6366F1',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                              }}
                                            >
                                              Edit
                                            </Button>
                                          )}
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                              )
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
                <TableBody sx={{ maxHeight: 500 }}>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography sx={{ fontWeight: 600, marginTop: '10px' }}>
                        Co-signature documents(
                        {getAllMyDocuments?.coSignatureDocuments?.length || 0}){' '}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ padding: 0 }}
                    >
                      <Box sx={{ maxHeight: 550, overflowY: 'auto' }}>
                        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableBody>
                            {getDocumentRes?.loading ? (
                              <TableCell colSpan={6}>
                                {Array.from({ length: 7 }).map((_, index) => (
                                  <Box
                                    textAlign="center"
                                    key={index}
                                  >
                                    <Typography
                                      variant="h4"
                                      color="initial"
                                    >
                                      <Skeleton animation="wave" />
                                    </Typography>
                                  </Box>
                                ))}
                              </TableCell>
                            ) : !getDocumentRes?.response?.data?.coSignatureDocuments?.length ? (
                              <TableRow>
                                <TableCell colSpan={8}>
                                  <Box
                                    textAlign="center"
                                    p={3}
                                  >
                                    <img
                                      src={emptyImg}
                                      alt="loader"
                                      style={{
                                        width: 620,
                                        height: 240,
                                        margin: 'auto',
                                        alignItems: 'center',
                                        alignContent: 'center',
                                        marginTop: 50,
                                        display: 'block',
                                      }}
                                    />
                                    <Typography
                                      color={'text.secondary'}
                                      variant="h6"
                                      fontWeight={'200'}
                                    >
                                      No results found
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              getAllMyDocuments?.coSignatureDocuments?.map(
                                (document: any, index: any) => {
                                  return (
                                    <TableRow
                                      hover
                                      key={index}
                                    >
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Checkbox
                                            checked={coSignDocument?.some(
                                              (item: any) => item?.uuid === document?.uuid
                                            )}
                                            onClick={(e: any) =>
                                              handleAddCoSignDocument(e, document)
                                            }
                                            disabled={!document?.approvedForSupervisorSignature}
                                          />
                                          <Typography variant="subtitle2">
                                            {document?.patientName}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Stack
                                          alignItems="center"
                                          direction="row"
                                          spacing={1}
                                        >
                                          <Grid
                                            container
                                            sx={{ alignItems: 'center' }}
                                          >
                                            <Box sx={{ marginRight: '4px', marginTop: '7px' }}>
                                              <DocumentIcons
                                                iconType={document?.formType
                                                  ?.split('_')?.[0]
                                                  ?.toLowerCase()}
                                              />
                                            </Box>

                                            <Box sx={{ display: 'flex' }}>
                                              <Typography variant="subtitle2">
                                                {document?.documentType}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        </Stack>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="subtitle2">
                                          {document?.providerName}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="subtitle2">
                                          {document?.dateOfService
                                            ? format(
                                                new Date(
                                                  new Date(document?.dateOfService)?.valueOf() +
                                                    new Date(
                                                      document?.dateOfService
                                                    )?.getTimezoneOffset() *
                                                      60 *
                                                      1000
                                                ),
                                                'dd MMM, yyyy'
                                              )
                                            : '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="subtitle2">
                                          {document?.signDate
                                            ? format(
                                                new Date(
                                                  new Date(document?.signDate)?.valueOf() +
                                                    new Date(
                                                      document?.signDate
                                                    )?.getTimezoneOffset() *
                                                      60 *
                                                      1000
                                                ),
                                                'dd MMM, yyyy'
                                              )
                                            : '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', ml: '-20px' }}>
                                          {/* THIN-7261: Rejected chip removed; chip
                                                state is now binary, driven by the
                                                canonical approvedForSupervisorSignature
                                                boolean instead of superviseStatus. */}
                                          {document?.approvedForSupervisorSignature ? (
                                            <Chip
                                              label={
                                                <Typography variant="overline">Approved</Typography>
                                              }
                                              variant="outlined"
                                              sx={{
                                                marginLeft: '10px',
                                                color: '#6366F1',
                                                backgroundColor: '#ECECFD',
                                              }}
                                            />
                                          ) : (
                                            <Chip
                                              label={
                                                <Typography variant="overline">
                                                  Pending Review
                                                </Typography>
                                              }
                                              variant="outlined"
                                              sx={{
                                                marginLeft: '10px',
                                                color: '#6C737F',
                                                backgroundColor: 'rgba(17, 25, 39, 0.08)',
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Button
                                          title="Complete Document"
                                          onClick={() => {
                                            navigate('doc-preview-carousel', {
                                              state: {
                                                document:
                                                  getAllMyDocuments?.coSignatureDocuments || [],
                                                index: index,
                                              },
                                            });
                                          }}
                                          size="small"
                                          sx={{
                                            color: ' #6366F1',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                          }}
                                        >
                                          Review
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                              )
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </>
        {/* </Scrollbar> */}
      </Card>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onMouseLeave={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Box onMouseLeave={handleClose}>
          <Typography>
            <Button
              onClick={() => goToEoc(getCurrentItem)}
              endIcon={<RightIcon />}
            >
              Go to Episode of Care
            </Button>
          </Typography>
        </Box>
      </Menu>

      {/* clock in modal */}
      {canShowClockInDialog() && (
        <BootstrapDialog
          PaperProps={{ style: { maxWidth: '750px' } }}
          onClose={() => setIsClockIn(false)}
          aria-labelledby="customized-dialog-title"
          open={isClockIn && !notToShowAgain}
        >
          <ClockInOutModal
            close={handleIsClock}
            handleClockIn={HandleStartClock}
          />
        </BootstrapDialog>
      )}

      <BootstrapDialog
        PaperProps={{ style: { width: '600px' } }}
        onClose={handleCloseNewSignModel}
        aria-labelledby="customized-dialog-title"
        open={openNewSignModel}
      >
        <MySignatureModal
          onClose={handleCloseNewSignModel}
          signDocument={signDocument}
          coSignDocument={coSignDocument}
          pinError={bulkPinError}
        />
      </BootstrapDialog>
      <SigningLockedDialog
        open={bulkLocked.open}
        lockedUntil={bulkLocked.until}
        onClose={() => setBulkLocked({ open: false, until: null })}
      />
      {/* For selecting a supervisor */}
      <Modal
        style={modalStyle}
        open={triggerSelectSupervisor}
        onClose={closeSupervisorModal}
      >
        <div>
          <SelectSuperVisor
            activeTab={providerInfo?.discipline}
            close={closeSupervisorModal}
            selectedDocument={selectedDocument}
            role={role}
            source="OTHER_SECTION"
          />
        </div>
      </Modal>

      <Snackbar
        open={Boolean(exportError)}
        autoHideDuration={8000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setExportError(null)}
        >
          {exportError}
        </Alert>
      </Snackbar>

      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </>
  );
};

export default MySignatureManagement;